import React, { useEffect, useRef, useState } from 'react';
import { paidTotal, tuitionPaid, grade, gradeColor } from '../db';
import { Badge, Card, Avatar, Stat, NoData, TblWrap } from '../ui.jsx';
import { useCountUp, StatCard, FeeChart } from '../components/StatCard';

// ── Dashboard sub-components (must be outside Dashboard to obey Rules of Hooks) ──

function QuickStatItem({ item, setPage }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div onClick={() => setPage(item.page)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? item.color + '12' : 'var(--s1)', border: `1.5px solid ${hov ? item.color + '50' : 'var(--br)'}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 180ms', display: 'flex', alignItems: 'center', gap: 12, boxShadow: hov ? `0 4px 16px ${item.color}18` : 'none', transform: hov ? 'translateX(2px)' : 'translateX(0)' }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: item.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: item.color }}>{item.icon}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx)', fontFamily: 'Manrope,sans-serif', lineHeight: 1 }}>{item.val}</div>
        <div style={{ fontSize: 10, color: 'var(--di)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</div>
      </div>
      <span className="material-symbols-outlined" style={{ fontSize: 14, color: item.color + '80', flexShrink: 0, transition: 'transform 180ms', transform: hov ? 'translateX(2px)' : 'translateX(0)' }}>arrow_forward</span>
    </div>
  );
}

function StuFeeRow({ s, pays, setPage }) {
  const [hov, setHov] = React.useState(false);
  const paid = paidTotal(pays, s.id);
  const fst = paid >= s.fee && s.fee > 0 ? 'Paid' : s.fst;
  const stColor = fst === 'Paid' ? { bg: 'var(--gnl)', tx: 'var(--gn)' } : fst === 'Overdue' ? { bg: 'var(--rdl)', tx: 'var(--rd)' } : { bg: 'var(--ywl)', tx: 'var(--yw)' };
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '0 16px', alignItems: 'center', padding: '8px 6px', borderRadius: 9, background: hov ? 'var(--s2)' : 'transparent', transition: 'background 150ms' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--bl)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
          {(s.fn[0] || '') + (s.ln[0] || '')}
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)' }}>{s.fn} {s.ln}</span>
      </div>
      <span style={{ fontSize: 11, color: 'var(--di)' }}>{s.cls || '—'}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)' }}>₹{(s.fee || 0).toLocaleString('en-IN')}</span>
      <span style={{ fontSize: 9, fontWeight: 700, color: stColor.tx, background: stColor.bg, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>{fst}</span>
    </div>
  );
}

function ExamItem({ e, i }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: hov ? 'var(--bll)' : 'var(--bg)', border: '1.5px solid', borderColor: hov ? 'var(--scl)' : 'var(--br)', transition: 'all 160ms' }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: '#1960a318', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#1960a3' }}>quiz</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--bl)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name || e.sub || 'Exam'}</div>
        <div style={{ fontSize: 10, color: 'var(--di)' }}>{e.cls || 'All'} · {e.dt || 'TBD'}</div>
      </div>
      <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--sc)', background: 'var(--scl)', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>Upcoming</span>
    </div>
  );
}

function ClassRow({ c }) {
  const [hov, setHov] = React.useState(false);
  const bc = c.pct === null ? '#9aa0a6' : c.pct >= 90 ? '#059669' : c.pct >= 75 ? '#cb9524' : '#ba1a1a';
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'grid', gridTemplateColumns: '1fr 50px 60px 80px', gap: '0 12px', alignItems: 'center', padding: '8px 6px', borderRadius: 9, background: hov ? 'var(--s2)' : 'transparent', transition: 'background 150ms' }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--bl)' }}>{c.name}</div>
        <div style={{ fontSize: 10, color: 'var(--di)' }}>{c.teacher ? c.teacher.fn + ' ' + c.teacher.ln : 'No teacher'}</div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)' }}>{c.count}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: bc }}>{c.pct !== null ? c.pct + '%' : '—'}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx)' }}>₹{c.collected.toLocaleString('en-IN')}</span>
    </div>
  );
}

function PayRow({ p, students }) {
  const [hov, setHov] = React.useState(false);
  const stu = students.find(s => s.id === p.sid);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: hov ? 'var(--s2)' : 'transparent', transition: 'background 150ms' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bl)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
        {stu ? (stu.fn[0] || '') + (stu.ln[0] || '') : '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stu ? stu.fn + ' ' + stu.ln : 'Unknown'}</div>
        <div style={{ fontSize: 10, color: 'var(--di)' }}>{p.dt} · {p.mn || p.ty || 'Fee'}</div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 800, color: '#059669', flexShrink: 0 }}>₹{(p.amt || 0).toLocaleString('en-IN')}</span>
    </div>
  );
}

function TeacherCard({ t, photos, students }) {
  const [hov, setHov] = React.useState(false);
  const photo = (photos || {})[t.id];
  const stuCount = students.filter(s => s.cls === t.cls).length;
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${hov ? 'var(--scl)' : 'var(--br)'}`, background: hov ? 'var(--bll)' : 'var(--s1)', transition: 'all 160ms' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bl)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, overflow: 'hidden' }}>
        {photo ? <img src={photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (t.fn[0] || '') + (t.ln[0] || '')}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--bl)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.fn} {t.ln}</div>
        <div style={{ fontSize: 10, color: 'var(--di)' }}>{t.cls || '—'} · {stuCount} students</div>
      </div>
    </div>
  );
}

function AttClassCard({ c }) {
  const [hov, setHov] = React.useState(false);
  const barColor = c.pct === null ? 'var(--br)' : c.pct >= 90 ? '#059669' : c.pct >= 75 ? '#cb9524' : '#ba1a1a';
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ padding: '14px 16px', borderRadius: 12, border: `1.5px solid ${hov ? barColor + '60' : 'var(--br)'}`, background: hov ? barColor + '08' : 'var(--s1)', transition: 'all 180ms' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--bl)' }}>{c.name}</span>
        <span style={{ fontSize: 12, fontWeight: 800, color: barColor }}>{c.pct !== null ? c.pct + '%' : '—'}</span>
      </div>
      <div style={{ height: 6, background: 'var(--br)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: (c.pct || 0) + '%', background: barColor, borderRadius: 3, transition: 'width 600ms cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
      <div style={{ fontSize: 10, color: 'var(--di)', marginTop: 6 }}>{c.stuCount} student{c.stuCount !== 1 ? 's' : ''}</div>
    </div>
  );
}

// Scroll-reveal hook — re-triggers every time element enters/leaves viewport
function useReveal(threshold = 0.12) {
  const ref = React.useRef(null);
  const [vis, setVis] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { setVis(e.isIntersecting); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

// Animated reveal wrapper
function Reveal({ children, delay = 0, dir = 'up' }) {
  const [ref, vis] = useReveal();
  const tx = dir === 'up' ? 'translateY(28px)' : dir === 'left' ? 'translateX(-28px)' : dir === 'right' ? 'translateX(28px)' : 'translateY(0)';
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? 'translate(0)' : tx, transition: `opacity 500ms ${delay}ms cubic-bezier(0.4,0,0.2,1), transform 500ms ${delay}ms cubic-bezier(0.4,0,0.2,1)` }}>
      {children}
    </div>
  );
}

function Dashboard({ db, save, setPage }) {
  const classFeeMap = db.settings?.classFees || {};
  const getAnnualFee = (s) => {
    const cf = classFeeMap[s.cls] || {};
    const monthly = parseFloat(cf.monthly) || 0;
    const exam = parseFloat(cf.exam) || 0;
    const base = monthly > 0 ? monthly * 12 + exam : 0;
    const totalConcession = db.pays.filter(p => p.sid === s.id && p.concession).reduce((sum, p) => sum + (p.concession || 0), 0);
    return Math.max(0, base - totalConcession);
  };
  const col = db.pays.reduce((s, p) => s + p.amt, 0);
  const totalFee = db.students.reduce((s, x) => s + getAnnualFee(x), 0);
  const pen = db.students.reduce((s, x) => {
    const annualFee = getAnnualFee(x);
    const paid = tuitionPaid(db.pays, x.id);
    return s + Math.max(0, annualFee - paid);
  }, 0);
  const penStu = db.students.filter(s => {
    const annualFee = getAnnualFee(s);
    const paid = tuitionPaid(db.pays, s.id);
    return annualFee > 0 && paid < annualFee;
  }).slice(0, 5);
  const tuitionCollected = db.pays.reduce((s, p) => s + (p.tuition || 0), 0);
  const colPct = totalFee > 0 ? Math.round(tuitionCollected / totalFee * 100) : 0;

  // Extra computed data
  const fullyPaid = db.students.filter(s => tuitionPaid(db.pays, s.id) >= getAnnualFee(s) && getAnnualFee(s) > 0).length;
  const totalStudents = db.students.length;
  const upcomingExams = db.exams.filter(e => e.st === 'Upcoming');

  // Attendance overall
  let attPresent = 0, attTotal = 0;
  Object.values(db.att).forEach(day => Object.values(day).forEach(v => { attTotal++; if (v === 'P' || v === 'L') attPresent++; }));
  const attPct = attTotal > 0 ? Math.round(attPresent / attTotal * 100) : null;

  const quickActions = [
    { icon: 'person_add', label: 'New Admission', val: 'Add student', page: 'stu', color: '#1960a3' },
    { icon: 'payments', label: 'Collect Fees', val: pen > 0 ? `₹${pen.toLocaleString('en-IN')} pending` : 'All clear', page: 'fee', color: '#cb9524' },
    { icon: 'fact_check', label: 'Attendance', val: attPct !== null ? `${attPct}% overall` : 'No records', page: 'att', color: '#059669' },
    { icon: 'event_note', label: 'Exams', val: upcomingExams.length ? `${upcomingExams.length} upcoming` : 'No schedule', page: 'exam', color: '#7c3aed' },
  ];

  const adminHighlights = [
    { label: 'Students', value: totalStudents, sub: `${db.classes.length} active classes`, accent: '#1960a3', page: 'stu' },
    { label: 'Teachers', value: db.teachers.length, sub: `${[...new Set(db.teachers.map(t => t.su).filter(Boolean))].length} subjects`, accent: '#059669', page: 'tea' },
    { label: 'Collection', value: `₹${col.toLocaleString('en-IN')}`, sub: totalFee > 0 ? `${colPct}% of planned fees` : 'No fee plan set', accent: '#cb9524', page: 'fee' },
    { label: 'Pending', value: `₹${pen.toLocaleString('en-IN')}`, sub: `${penStu.length} students pending`, accent: '#ba1a1a', page: 'fee' },
  ];

  const panelStyle = {
    background: 'var(--s1)',
    borderRadius: 22,
    padding: '22px 24px',
    border: '1px solid #e5ebf3',
    boxShadow: '0 4px 18px rgba(0,32,69,0.05)',
    height: '100%',
  };

  const sectionHeader = (title, subtitle, action) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif', lineHeight: 1.15 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--di)', marginTop: 4, lineHeight: 1.55 }}>{subtitle}</div>
      </div>
      {action}
    </div>
  );

  return (
    <div className="dashboard-shell" style={{ width: '100%' }}>
      <div className="dashboard-hero" style={{ background: 'linear-gradient(135deg,#08294a,#15508e)', color: '#fff', borderRadius: 24, padding: '26px 28px', boxShadow: '0 18px 42px rgba(8,41,74,0.16)', marginBottom: 20 }}>
        <div className="dashboard-hero-inner" style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 700 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.68)', marginBottom: 8 }}>
              Admin Dashboard · {db.settings.year || '2025-26'}
            </div>
            <h2 style={{ fontSize: 30, fontWeight: 900, fontFamily: 'Manrope,sans-serif', lineHeight: 1.05, margin: 0 }}>
              Daily school operations at a glance
            </h2>
            <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.7, color: 'rgba(255,255,255,0.74)', maxWidth: 640 }}>
              Focus on admissions, collections, attendance, and the work that needs follow-up today.
            </p>
          </div>
          <div className="dashboard-hero-actions" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setPage('stu')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 18px', borderRadius: 12, border: 'none', background: '#fbbf24', color: '#132238', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>
              New Admission
            </button>
            <button onClick={() => setPage('rep')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
              Export Reports
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
        {adminHighlights.map((card, i) => (
          <Reveal key={card.label} delay={i * 50}>
            <div onClick={() => setPage(card.page)} style={{ background: 'var(--s1)', borderRadius: 18, padding: '18px 18px', border: '1px solid #e5ebf3', boxShadow: '0 3px 14px rgba(0,32,69,0.045)', cursor: 'pointer', minHeight: 150, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: card.accent + '16', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: card.accent }} />
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>arrow_outward</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 28, lineHeight: 1, fontWeight: 900, color: '#0f172a', fontFamily: 'Manrope,sans-serif' }}>{card.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, lineHeight: 1.5 }}>{card.sub}</div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="dashboard-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'stretch' }}>
        <Reveal>
          <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
            {sectionHeader('Quick Actions', 'Jump straight into the tasks that matter today.', null)}
            <div style={{ display: 'grid', gap: 10, flex: 1 }}>
              {quickActions.map(item => <QuickStatItem key={item.label} item={item} setPage={setPage} />)}
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
            {sectionHeader('Key Status', 'Only the essential operational indicators.', null)}
            <div style={{ display: 'grid', gap: 10, flex: 1 }}>
              {[
                { label: 'Collection rate', value: `${colPct}%`, tone: '#1960a3', bg: '#eef4ff' },
                { label: 'Fully paid students', value: fullyPaid, tone: '#059669', bg: '#ecfdf5' },
                { label: 'Pending students', value: penStu.length, tone: '#ba1a1a', bg: '#fff1f2' },
                { label: 'Active classes', value: db.classes.length, tone: '#7c3aed', bg: '#f5f3ff' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: item.bg, borderRadius: 14, padding: '13px 14px', minHeight: 70 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{item.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 900, color: item.tone, fontFamily: 'Manrope,sans-serif' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}


export default Dashboard;
